import i18n from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import { createContext, useContext, useEffect, useState } from 'react';

// Import all 27 locale files
import enGB from './locales/en-GB.json';
import enUS from './locales/en-US.json';
import af from './locales/af.json';
import id from './locales/id.json';
import ms from './locales/ms.json';
import da from './locales/da.json';
import de from './locales/de.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import it from './locales/it.json';
import nl from './locales/nl.json';
import pl from './locales/pl.json';
import ptBR from './locales/pt-BR.json';
import ptPT from './locales/pt-PT.json';
import ru from './locales/ru.json';
import ro from './locales/ro.json';
import sk from './locales/sk.json';
import sv from './locales/sv.json';
import vi from './locales/vi.json';
import tr from './locales/tr.json';
import el from './locales/el.json';
import hi from './locales/hi.json';
import zhCN from './locales/zh-CN.json';
import zhTW from './locales/zh-TW.json';
import ja from './locales/ja.json';
import ko from './locales/ko.json';
import ar from './locales/ar.json';

// All 27 supported languages with flags
export const SUPPORTED_LANGUAGES = [
  { code: 'en-US', name: 'English (US)', nativeName: 'English (US)', dir: 'ltr', flag: 'US' },
  { code: 'en-GB', name: 'English (UK)', nativeName: 'English (UK)', dir: 'ltr', flag: 'GB' },
  { code: 'af', name: 'Afrikaans', nativeName: 'Afrikaans', dir: 'ltr', flag: 'ZA' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', dir: 'rtl', flag: 'SA' },
  { code: 'da', name: 'Danish', nativeName: 'Dansk', dir: 'ltr', flag: 'DK' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', dir: 'ltr', flag: 'DE' },
  { code: 'el', name: 'Greek', nativeName: 'Ελληνικά', dir: 'ltr', flag: 'GR' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', dir: 'ltr', flag: 'ES' },
  { code: 'fr', name: 'French', nativeName: 'Français', dir: 'ltr', flag: 'FR' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', dir: 'ltr', flag: 'IN' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', dir: 'ltr', flag: 'ID' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', dir: 'ltr', flag: 'IT' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', dir: 'ltr', flag: 'JP' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', dir: 'ltr', flag: 'KR' },
  { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu', dir: 'ltr', flag: 'MY' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', dir: 'ltr', flag: 'NL' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski', dir: 'ltr', flag: 'PL' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)', nativeName: 'Português (Brasil)', dir: 'ltr', flag: 'BR' },
  { code: 'pt-PT', name: 'Portuguese (Portugal)', nativeName: 'Português (Portugal)', dir: 'ltr', flag: 'PT' },
  { code: 'ro', name: 'Romanian', nativeName: 'Română', dir: 'ltr', flag: 'RO' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', dir: 'ltr', flag: 'RU' },
  { code: 'sk', name: 'Slovak', nativeName: 'Slovenčina', dir: 'ltr', flag: 'SK' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska', dir: 'ltr', flag: 'SE' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', dir: 'ltr', flag: 'TR' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', dir: 'ltr', flag: 'VN' },
  { code: 'zh-CN', name: 'Chinese (Simplified)', nativeName: '简体中文', dir: 'ltr', flag: 'CN' },
  { code: 'zh-TW', name: 'Chinese (Traditional)', nativeName: '繁體中文', dir: 'ltr', flag: 'TW' }
];

const resources = {
  'en-US': { translation: enUS },
  'en-GB': { translation: enGB },
  'af': { translation: af },
  'ar': { translation: ar },
  'da': { translation: da },
  'de': { translation: de },
  'el': { translation: el },
  'es': { translation: es },
  'fr': { translation: fr },
  'hi': { translation: hi },
  'id': { translation: id },
  'it': { translation: it },
  'ja': { translation: ja },
  'ko': { translation: ko },
  'ms': { translation: ms },
  'nl': { translation: nl },
  'pl': { translation: pl },
  'pt-BR': { translation: ptBR },
  'pt-PT': { translation: ptPT },
  'ro': { translation: ro },
  'ru': { translation: ru },
  'sk': { translation: sk },
  'sv': { translation: sv },
  'tr': { translation: tr },
  'vi': { translation: vi },
  'zh-CN': { translation: zhCN },
  'zh-TW': { translation: zhTW }
};

// Safe language detection that won't crash on old WebViews
const safeLanguageDetector = {
  type: 'languageDetector',
  async: false,
  init: function() {},
  detect: function() {
    // Priority 1: URL query param
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const langParam = urlParams.get('lang');
      if (langParam && SUPPORTED_LANGUAGES.find(l => l.code === langParam)) {
        return langParam;
      }
    } catch (e) {
      console.debug('[i18n] URL param detection failed:', e);
    }
    
    // Priority 2: localStorage
    try {
      const stored = localStorage.getItem('parachpay_language');
      if (stored && SUPPORTED_LANGUAGES.find(l => l.code === stored)) {
        return stored;
      }
    } catch (e) {
      console.debug('[i18n] localStorage detection failed:', e);
    }
    
    // Priority 3: navigator.language (guarded)
    try {
      const navLang = navigator.language || navigator.userLanguage;
      if (navLang) {
        // Try exact match first
        if (SUPPORTED_LANGUAGES.find(l => l.code === navLang)) {
          return navLang;
        }
        // Try base language (e.g., 'zh' from 'zh-CN')
        const baseLang = navLang.split('-')[0];
        const match = SUPPORTED_LANGUAGES.find(l => l.code.startsWith(baseLang));
        if (match) {
          return match.code;
        }
      }
    } catch (e) {
      console.debug('[i18n] Navigator language detection failed:', e);
    }
    
    // Fallback
    return 'en-US';
  },
  cacheUserLanguage: function(lng) {
    try {
      localStorage.setItem('parachpay_language', lng);
    } catch (e) {
      console.debug('[i18n] Failed to cache language:', e);
    }
  }
};

// Initialize i18next with safe detector
i18n
  .use(safeLanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en-US',
    supportedLngs: SUPPORTED_LANGUAGES.map(l => l.code),
    interpolation: {
      escapeValue: false
    },
    react: {
      useSuspense: false
    }
  });

// Context for additional i18n helpers
const I18nContext = createContext();

export const I18nProvider = ({ children }) => {
  const [currentLang, setCurrentLang] = useState(() => {
    try {
      return i18n.language || 'en-US';
    } catch {
      return 'en-US';
    }
  });

  useEffect(() => {
    const handleLanguageChange = (lng) => {
      try {
        setCurrentLang(lng);
        const langConfig = SUPPORTED_LANGUAGES.find(l => l.code === lng);
        
        // Set document direction for RTL languages
        document.documentElement.dir = langConfig?.dir || 'ltr';
        document.documentElement.lang = lng;
        
        // Persist to localStorage (guarded)
        try {
          localStorage.setItem('parachpay_language', lng);
        } catch (e) {
          console.debug('[i18n] Failed to persist language:', e);
        }
      } catch (e) {
        console.warn('[i18n] Language change handler error:', e);
      }
    };

    // Set initial direction
    try {
      const langConfig = SUPPORTED_LANGUAGES.find(l => l.code === currentLang);
      document.documentElement.dir = langConfig?.dir || 'ltr';
      document.documentElement.lang = currentLang;
    } catch (e) {
      console.debug('[i18n] Initial direction setup failed:', e);
    }

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      try {
        i18n.off('languageChanged', handleLanguageChange);
      } catch {}
    };
  }, [currentLang]);

  const changeLanguage = (langCode) => {
    try {
      if (SUPPORTED_LANGUAGES.find(l => l.code === langCode)) {
        i18n.changeLanguage(langCode);
      }
    } catch (e) {
      console.warn('[i18n] changeLanguage failed:', e);
    }
  };

  const getCurrentLanguage = () => {
    try {
      return SUPPORTED_LANGUAGES.find(l => l.code === currentLang) || SUPPORTED_LANGUAGES[0];
    } catch {
      return SUPPORTED_LANGUAGES[0];
    }
  };

  const isRTL = (() => {
    try {
      return getCurrentLanguage().dir === 'rtl';
    } catch {
      return false;
    }
  })();

  return (
    <I18nContext.Provider value={{
      language: currentLang,
      changeLanguage,
      getCurrentLanguage,
      supportedLanguages: SUPPORTED_LANGUAGES,
      isRTL
    }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    // Return safe defaults instead of throwing
    return {
      language: 'en-US',
      changeLanguage: () => {},
      getCurrentLanguage: () => SUPPORTED_LANGUAGES[0],
      supportedLanguages: SUPPORTED_LANGUAGES,
      isRTL: false
    };
  }
  return context;
};

// Re-export useTranslation from react-i18next for convenience
export { useTranslation };

export default i18n;
